import { SILENT_RENEW } from "./sts.js";

// Each tab keeps its own refresh-token chain in sessionStorage. "Duplicate tab" copies sessionStorage,
// so a copy finds its original's chain id; two tabs spending one rotating refresh token would make the
// STS revoke the chain. A tab holds a Web Lock named for its chain id for as long as it lives, and a
// tab that can't get that lock is a duplicate and starts a chain of its own.
export const CHAIN_ID_KEY = "tp.chainId";
export const TAB_LOCK_WAIT_MS = 1000;

export const chainLockName = (clientId, chainId) => `tp.chain:${clientId}:${chainId}`;

// Resolves true once the lock is held (kept until `until` settles), false if it stayed taken for waitMs.
const holdLock = (locks, name, waitMs, until) => new Promise((resolve, reject) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), waitMs);
    locks
        .request(name, { signal: controller.signal }, () => {
            clearTimeout(timer);
            resolve(true);
            return Promise.resolve(until).catch(() => {});
        })
        .catch((error) => {
            clearTimeout(timer);
            if (error?.name === "AbortError") resolve(false);
            else reject(error);
        });
});

// A reload's old page can still hold the lock for a moment, hence waiting rather than ifAvailable.
// Without Web Locks, or if they fail, every tab owns its chain: today's behaviour, never a page that can't load.
// A framed page shares its parent's sessionStorage and chain: it claims nothing and must not touch either.
export async function claimTabChain({
    storage,
    locks,
    clientId,
    waitMs = TAB_LOCK_WAIT_MS,
    until = new Promise(() => {}),
    newId = () => crypto.randomUUID(),
    framed = false,
}) {
    if (framed) return "framed";
    if (!locks) return "owner";
    // Once the wait has timed out the chain is known to be live elsewhere, so a later failure is still a duplicate.
    let taken = false;
    try {
        let chainId = storage.getItem(CHAIN_ID_KEY);
        if (!chainId) {
            chainId = newId();
            storage.setItem(CHAIN_ID_KEY, chainId);
        }
        if (await holdLock(locks, chainLockName(clientId, chainId), waitMs, until)) return "owner";

        taken = true;
        chainId = newId();
        storage.setItem(CHAIN_ID_KEY, chainId);
        await holdLock(locks, chainLockName(clientId, chainId), waitMs, until);
        return "duplicate";
    } catch {
        return taken ? "duplicate" : "owner";
    }
}

export const renewSettingsFor = (claim) => (claim === "duplicate" || claim === "framed" ? { automaticSilentRenew: false } : SILENT_RENEW);
